import {openDeleteDialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray, isFalse, noop} from 'ramda-adjunct';

import {useDeleteVehicleWarehouseMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid, usePermissions} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['name'] as const;

export function VehicleWarehouseGrid() {
  const navigate = useNavigate();
  const [deleteVehicleWarehouse] = useDeleteVehicleWarehouseMutation();

  const [canCreateVehicleWarehouse, canEditVehicleWarehouse] = usePermissions({
    permissionKeys: ['vehicleWarehouseCreate', 'vehicleWarehouseEdit'],
  });

  const onDelete = (vehicleWarehouseId: string, name: string | undefined) => {
    openDeleteDialog({
      text: i18n.t('entity.vehicleWarehouse.labels.confirmDeleteText', {name}),
      onConfirm: () =>
        deleteVehicleWarehouse({vehicleWarehouseId})
          .unwrap()
          .then(() => refreshDmsDatagrid('vehicle-warehouse'))
          .catch(handleApiError),
    });
  };

  const actions: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      throw new Error('rowId for vehicle warehouse DG not present');
    }

    const name = getColumnData<string>('name')?.value;

    match(actionKey)
      .with('detail', 'edit', () => {
        if (isFalse(canEditVehicleWarehouse)) {
          showNotification.error(i18n.t('general.labels.noPermission'));
          return noop();
        }
        return navigate(
          composePath(settingsRoutes.vehicleWarehouseEdit, {
            params: {
              id,
            },
          })
        );
      })
      .with('delete', () => onDelete(id, name))
      .otherwise(noop);
  };

  return (
    <Box minHeight={40}>
      <DmsDataGrid
        data-testid={testIds.settings.vehicleWarehouseManagement('vehicleWarehouseGrid')}
        gridCode="vehicle-warehouse"
        predefinedColumns={PREDEFINED_COLUMNS}
        actionCallback={actions}
        emptyState={{
          headline: i18n.t('page.vehicleWarehouseManagement.noWarehouses'),
          subheadline: i18n.t('page.vehicleWarehouseManagement.labels.createFirstWarehouse'),
          action: canCreateVehicleWarehouse
            ? {
                title: i18n.t('page.vehicleWarehouseManagement.actions.createWarehouse'),
                onClick: () => {
                  navigate(settingsRoutes.vehicleWarehouseCreate);
                },
              }
            : undefined,
        }}
        autoHeight
      />
    </Box>
  );
}
